import React from "react";
import { Placeholder } from "@/components/Global";
import TrackSwitcher from "./TrackSwitcher";
import LearningTimeButton from "./LearningTimeButton";
import leftChevron from "@/images/icons/circle-left-chevron.svg";
import rightChevron from "@/images/icons/circle-right-chevron.svg";
import lessonIcon from "@/images/icons/lesson-icon.svg";
import studentIcon from "@/images/icons/student-icon.svg";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./PlannerHeader.scss";
import RibbonHeader from "./RibbonHeader";

interface PlannerHeaderProps {
  curriculumIcon: string;
  curriculumName: string;
  loading: boolean;
  disableWeekSwitching: boolean;
  weekNum: number;
  dateFrom: Date;
  dateTo: Date;
  prevWeekHandler: () => void;
  nextWeekHandler: () => void;
  onLearningTimeClick: () => void;
  isFullHeader: boolean;
  handleStudentPlaylistPress?: () => void;
  totalBookingAlerts?: number;
  handlePlannerPress?: () => void;
}

const PlannerHeader: React.FC<PlannerHeaderProps> = ({
  loading,
  disableWeekSwitching,
  dateFrom,
  dateTo,
  prevWeekHandler,
  nextWeekHandler,
  handleStudentPlaylistPress,
  handlePlannerPress,
  isFullHeader,
  totalBookingAlerts,
}) => {
  const { t } = useTranslation("planner");
  const location = useLocation();

  const renderPlanStatus = (startDate: Date, endDate: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    if (endDate < today) return null;

    const isCurrentWeek = today >= startDate && today <= endDate;

    if (isCurrentWeek) return <RibbonHeader text={"This week's plan"}/>

    const nextWeekStart = new Date(today);
    nextWeekStart.setDate(today.getDate() + (7 - today.getDay()));
    const isNextWeek =
      startDate >= nextWeekStart &&
      startDate < new Date(nextWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

    return isNextWeek ? (
      <RibbonHeader text={"Reviewing next week's plan"} future/>
    ) : (
      <RibbonHeader text={"Reviewing future plan"} future/>
    );
  };


const shouldShowRibbon = () => {
  const today = new Date();
  return dateTo.setHours(0, 0, 0, 0) >= today.setHours(0, 0, 0, 0); 
};

const getBookingAlerts = () => {
  if (!totalBookingAlerts) return 0;
  
  return totalBookingAlerts;
};
const alertsCount = getBookingAlerts();
const dateFromMonthLocalized = `${dateFrom.toLocaleString("default", { month: "short" })}${dateFrom.getMonth() !== 4 ? '.' : ''}`;
const dateToMonthLocalized = `${dateTo.toLocaleString("default", { month: "short" })}${dateFrom.getMonth() !== 4 ? '.' : ''}` ;

  return (
    <header className="page-subheader">
      {shouldShowRibbon() && renderPlanStatus(dateFrom, dateTo)}
      <nav
        className={`date-range-container col col-nav ${!isFullHeader ? "flex-fill" : ""}`}
      >
        <button
          type="button"
          onClick={prevWeekHandler}
          disabled={disableWeekSwitching}
        >
          <img src={leftChevron} alt="Previous Week" />
        </button>
        <div>
          <span className="planner-date-range">
          {`${dateFromMonthLocalized} ${dateFrom.getDate()} - ${dateToMonthLocalized} ${dateTo.getDate()}`}
          </span>
        </div>
        <button
          type="button"
          onClick={nextWeekHandler}
          disabled={disableWeekSwitching}
        >
          <img src={rightChevron} alt="Next Week" />
        </button>
      </nav>

      {isFullHeader && !loading && (
        <div className='header-button-container'>
          <button
            type="button"
            onClick={handlePlannerPress}
            className={`btn-playlists${location.pathname === "/planner" ? " active" : ""}`}

          >
            <i className="icon">
              <img src={lessonIcon} />
            </i>
            <span style={{ fontSize: "24px" }}>By Lesson</span>
          </button>

          <button
            type="button"
            onClick={handleStudentPlaylistPress}
            className={`btn-playlists${location.pathname === "/planner/student-learning-playlist" ? " active" : ""}`}
          >
            <i className="icon">
              <img src={studentIcon} />
            </i>
            <span className="btn-label">By Student       
            </span>
            {alertsCount > 0 && 
            <span className="alert-count-indicator">
              <span className="alert-count-number">
              {alertsCount}
              </span>
            </span>}
          </button>
        </div>
      )}

      {isFullHeader && loading && <Placeholder colWidth={2} heightInRem={3} />}
    </header>
  );
};

export default PlannerHeader;
